import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import useClasses from "hooks/useClasses";
import { getAnalyticsSources, syncAnalyticsData } from "store/actions/analytics";
import styles from "./analyticsStyle";
import { CircularProgress, Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, useTheme } from "@mui/material";

export default function AnalyticsStatus() {
  const classes = useClasses(styles);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();
  const history = useHistory();
  const { sources, analyticsLoading } = useSelector((s) => s.analytics || {});

  const [selectedProviders, setSelectedProviders] = useState([]);

  const handleFetchStatus = () => {
    dispatch(getAnalyticsSources(history));
  };

  const handleSync = () => {
    if (selectedProviders.length === 0) {
      // If none selected, sync all available sources
      const allProviders = sourcesData.map(s => s.source);
      dispatch(syncAnalyticsData(allProviders, history));
    } else {
      dispatch(syncAnalyticsData(selectedProviders, history));
    }
  };

  useEffect(() => {
    handleFetchStatus();
  }, []);

  let sourcesData = [];
  if (Array.isArray(sources)) {
    sourcesData = sources;
  } else if (Array.isArray(sources?.sources)) {
    sourcesData = sources.sources;
  } else if (sources?.sources && typeof sources.sources === "object") {
    sourcesData = Object.entries(sources.sources).map(([key, val]) => ({
      source: key,
      ...val,
      last_sync: val.last_success_at || val.last_sync,
      is_configured: val.is_configured !== undefined ? val.is_configured : true
    }));
  } else if (sources && typeof sources === "object") {
    sourcesData = Object.entries(sources).map(([key, val]) => ({
      source: key,
      ...val,
      last_sync: val.last_success_at || val.last_sync,
      is_configured: val.is_configured !== undefined ? val.is_configured : true
    }));
  }

  const toggleProvider = (providerId) => {
    if (selectedProviders.includes(providerId)) {
      setSelectedProviders(selectedProviders.filter(p => p !== providerId));
    } else {
      setSelectedProviders([...selectedProviders, providerId]);
    }
  };

  return (
    <div className={classes.pageWrapper}>
      <Typography variant="h4" style={{ color: isDark ? "#fff" : "#333", marginBottom: 20 }}>Integration Status</Typography>

      <Box p={3} style={{ background: isDark ? "linear-gradient(145deg, #1e2128 0%, #262a33 100%)" : "#fff", borderRadius: "14px", border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.08)", marginBottom: "30px", boxShadow: isDark ? "none" : "0 2px 8px rgba(0,0,0,0.04)" }}>
        <Typography variant="h6" style={{ color: isDark ? "#fff" : "#333", marginBottom: 16 }}>Manual Sync (Queue)</Typography>
        <Typography variant="body2" style={{ color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)", marginBottom: 20 }}>
          Select specific providers to synchronize, or leave empty to sync all. This queues a background job.
        </Typography>

        <div className={classes.checkboxGroup} style={{ marginBottom: "20px", flexWrap: "wrap" }}>
          {sourcesData.map(s => (
            <label key={s.source} className={classes.checkboxLabel}>
              <input 
                type="checkbox" 
                className={classes.checkboxInput} 
                checked={selectedProviders.includes(s.source)} 
                onChange={() => toggleProvider(s.source)} 
              />
              {s.source}
            </label>
          ))}
        </div>

        <Box display="flex" gap={2}>
          <button className={classes.refreshBtn} onClick={handleSync} disabled={analyticsLoading}>
            {analyticsLoading ? "Queuing Sync..." : "Sync Selected Data"}
          </button>
          <button 
            className={classes.refreshBtn} 
            onClick={handleFetchStatus} 
            disabled={analyticsLoading} 
            style={{ background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)" }}
          >
            Refresh Status Table
          </button>
        </Box>
      </Box>

      {analyticsLoading ? (
        <div className={classes.loadingOverlay}><CircularProgress size={32} sx={{ color: "#6366f1", mr: 1.5 }} /> Loading data...</div>
      ) : (
        <TableContainer component={Paper} style={{ background: isDark ? "linear-gradient(145deg, #1e2128 0%, #262a33 100%)" : "#fff", border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.08)" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>Source</TableCell>
                <TableCell style={{ color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>Configured</TableCell>
                <TableCell style={{ color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>Status</TableCell>
                <TableCell align="right" style={{ color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>Last Sync</TableCell>
                <TableCell align="right" style={{ color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>Sync Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sourcesData.map((row, index) => {
                let statusColor = "rgba(255,255,255,0.7)";
                if (row.status === "active" || row.status === "ok") statusColor = "#34d399";
                if (row.status === "error" || row.status === "failed") statusColor = "#ef4444";
                if (row.status === "syncing") statusColor = "#3b82f6";
                
                return (
                  <TableRow key={index}>
                    <TableCell style={{ color: isDark ? "#fff" : "#333", fontWeight: "bold" }}>
                      {row.source}
                    </TableCell>
                    <TableCell style={{ color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)" }}>
                      {row.is_configured ? "Yes" : "No"}
                    </TableCell>
                    <TableCell style={{ color: statusColor, fontWeight: "bold" }}>
                      {row.status?.toUpperCase() || "UNKNOWN"}
                    </TableCell>
                    <TableCell align="right" style={{ color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)" }}>
                      {row.last_sync ? new Date(row.last_sync).toLocaleString() : "N/A"}
                    </TableCell>
                    <TableCell align="right" style={{ color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)", maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {row.last_sync_message || "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
              {sourcesData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" style={{ color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>No sources found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
}
